/**
 * Recipy Widget Loader
 *
 * Entry point for the widget library (~2KB gzipped).
 *
 * Responsibilities:
 * 1. Auto-detect API base URL from the script src attribute
 * 2. Register custom element tags (<recipy-recipe>, etc.)
 * 3. Initialize analytics (sendBeacon batching, impression tracking)
 * 4. Handle backward compatibility with legacy <div id="recipy-embed-123"> format
 *
 * Widget modules are code-split by Rollup into separate chunks.
 * Currently loaded eagerly at init time (fine for Phase 0 with one widget).
 * Data fetching is lazy (triggered by connectedCallback / IntersectionObserver).
 */

import { setApiBase, setPreviewToken } from './core/api';
import { initAnalytics } from './core/analytics';

// ===== API URL Detection =====

/**
 * Detect the API base URL from the loader script's src attribute.
 * Falls back to data-api attribute, then to production URL.
 */
function detectApiBase(): string {
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src || '';
    if (src.includes('recipy-widgets')) {
      // Check for explicit override
      const apiAttr = scripts[i].getAttribute('data-api');
      if (apiAttr) return apiAttr;

      // Auto-detect from script URL origin
      try {
        const url = new URL(src);
        // If hosted on CDN (widgets.recipyapp.com), point to the API server
        if (url.hostname === 'widgets.recipyapp.com') {
          return 'https://yeschef-backend.onrender.com';
        }
        return url.origin;
      } catch {
        // URL parsing failed
      }
    }
  }
  return 'https://yeschef-backend.onrender.com';
}

/**
 * Detect an optional preview token from the loader script's data-token attribute.
 * Used by the creator dashboard Quick Builder to preview private recipe widgets.
 */
function detectPreviewToken(): string | null {
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src || '';
    if (src.includes('recipy-widgets')) {
      return scripts[i].getAttribute('data-token');
    }
  }
  return null;
}

// ===== Lazy Widget Registration =====

/**
 * Map of element names to their module paths.
 * When a custom element is connected to the DOM, the loader dynamically
 * imports the corresponding module which contains the real implementation.
 */
const WIDGET_MODULES: Record<string, () => Promise<{ default?: unknown; [key: string]: unknown }>> = {
  'recipy-recipe': () => import('./widgets/recipe-card'),
  'recipy-ingredients': () => import('./widgets/ingredient-list'),
  'recipy-mealplan': () => import('./widgets/meal-plan'),
  'recipy-collection': () => import('./widgets/recipe-collection'),
  'recipy-shop': () => import('./widgets/shop'),
  'recipy-full-recipe': () => import('./widgets/full-recipe'),
  'recipy-creator': () => import('./widgets/creator-profile'),
  'recipy-mealplanner': () => import('./widgets/meal-planner'),
  'recipy-nutrition': () => import('./widgets/nutrition-label'),
  'recipy-credibility': () => import('./widgets/credibility-badge'),
  'recipy-quiz': () => import('./widgets/dietary-quiz'),
};

/**
 * Widget class name mapping (element name -> class export name).
 */
const WIDGET_CLASSES: Record<string, string> = {
  'recipy-recipe': 'RecipeCardWidget',
  'recipy-ingredients': 'IngredientListWidget',
  'recipy-mealplan': 'MealPlanWidget',
  'recipy-collection': 'RecipeCollectionWidget',
  'recipy-shop': 'ShopWidget',
  'recipy-full-recipe': 'FullRecipeWidget',
  'recipy-creator': 'CreatorProfileWidget',
  'recipy-mealplanner': 'MealPlannerWidget',
  'recipy-nutrition': 'NutritionLabelWidget',
  'recipy-credibility': 'CredibilityBadgeWidget',
  'recipy-quiz': 'DietaryQuizWidget',
};

// ===== Backward Compatibility =====

/**
 * Handle legacy <div id="recipy-embed-123"> format.
 * Finds these containers and replaces them with <recipy-recipe id="123">.
 */
function upgradeLegacyContainers(): void {
  const elements = document.querySelectorAll('[id^="recipy-embed-"]');
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const recipeId = el.id.replace('recipy-embed-', '');
    if (!recipeId || isNaN(parseInt(recipeId, 10))) continue;

    // Don't upgrade if already has a recipy-recipe child
    if (el.querySelector('recipy-recipe')) continue;

    // Create the new widget element
    const widget = document.createElement('recipy-recipe');
    widget.setAttribute('data-recipe-id', recipeId);

    // Copy theme from data attribute if present
    const theme = el.getAttribute('data-theme');
    if (theme) widget.setAttribute('theme', theme);

    // Replace content
    el.innerHTML = '';
    el.appendChild(widget);
  }
}

// ===== Lazy Module Loading =====

const loadingModules = new Set<string>();

/**
 * Load and register a single widget module on demand.
 * Deduplicates concurrent loads for the same element.
 */
async function loadAndRegister(elementName: string): Promise<void> {
  if (customElements.get(elementName) || loadingModules.has(elementName)) return;
  const loadModule = WIDGET_MODULES[elementName];
  if (!loadModule) return;

  loadingModules.add(elementName);
  try {
    const module = await loadModule();
    const className = WIDGET_CLASSES[elementName];
    const WidgetClass = module[className] as CustomElementConstructor | undefined;
    if (WidgetClass && !customElements.get(elementName)) {
      customElements.define(elementName, WidgetClass);
    }
  } catch (err) {
    console.error(`[recipy-widgets] Failed to register <${elementName}>:`, err);
  } finally {
    loadingModules.delete(elementName);
  }
}

/**
 * Scan a root element for widget tags and load their modules in parallel.
 */
function scanAndLoad(root: ParentNode): void {
  const needed: string[] = [];
  for (const elementName of Object.keys(WIDGET_MODULES)) {
    if (customElements.get(elementName)) continue;
    if (root.querySelector(elementName)) {
      needed.push(elementName);
    }
  }
  if (needed.length > 0) {
    // Load all needed modules in parallel (not sequentially)
    Promise.all(needed.map(name => loadAndRegister(name)));
  }
}

// ===== Initialization =====

/**
 * Main initialization function.
 * Called on DOMContentLoaded or immediately if DOM is already ready.
 */
function init(): void {
  // 1. Detect and set API base URL
  const apiBase = detectApiBase();
  setApiBase(apiBase);

  // 1b. Detect optional preview token (for creator dashboard widget previews)
  const token = detectPreviewToken();
  if (token) {
    setPreviewToken(token);
  }

  // 2. Initialize analytics
  initAnalytics();

  // 3. Scan DOM for widget elements and load only what's needed (in parallel)
  scanAndLoad(document);

  // 4. Watch for dynamically added widget elements
  const observer = new MutationObserver((mutations) => {
    let needsScan = false;
    for (const mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        const node = mutation.addedNodes[i];
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          const tag = el.tagName.toLowerCase();
          if (tag in WIDGET_MODULES && !customElements.get(tag)) {
            loadAndRegister(tag);
          }
          // Also check children of the added node
          if (el.children.length > 0) {
            needsScan = true;
          }
        }
      }
    }
    if (needsScan) scanAndLoad(document);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // 5. Handle backward-compatible legacy containers
  upgradeLegacyContainers();
}

// ===== Entry Point =====

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
